import { useEffect, useRef } from "react";
import { logger } from "@/utils/logger";
import { CreatePageAction } from "@/stores/createPageStore";

interface UseAutoTicketManagementProps {
  state: {
    frontVariations: Array<{
      imageUrl: string;
      imagePath: string | null;
    }> | null;
    backVariations: Array<{
      imageUrl: string;
      imagePath: string | null;
    }> | null;
    selectedFrontVariationIndex: number | null;
    selectedBackVariationIndex: number | null;
    backStale: boolean;
    persistedBackTicketId: string | null;
    ticketId: string | null;
    imageUrl: string | null;
    imagePath: string | null;
    loading: boolean;
    isModifying: boolean;
    formData: any;
  };
  dispatch: React.Dispatch<CreatePageAction>;
  updateGeneratedTicket: (
    ticketId: string,
    frontImageUrl: string,
    frontImagePath: string | null,
    backImageUrl?: string | null,
    backImagePath?: string | null,
    modificationPrompt?: string,
    isModifyingFront?: boolean
  ) => Promise<void>;
}

export function useAutoTicketManagement({
  state,
  updateGeneratedTicket,
}: UseAutoTicketManagementProps) {
  // Guard refs to prevent race conditions in auto-update ticket effects
  const isUpdatingTicketRef = useRef(false);

  // Tickets are persisted by generate-image before new variations are
  // returned. Keep this effect only as a recovery path for legacy/restored
  // back variations that have no server-returned ticket association.
  useEffect(() => {
    const autoUpdateBackTicket = async () => {
      // Guard against concurrent execution and the normal transactional
      // generation path, which must not race its server commit with a PUT.
      if (
        isUpdatingTicketRef.current ||
        state.persistedBackTicketId ||
        state.backStale
      ) {
        return;
      }

      // Only auto-update if:
      // 1. Back variations exist
      // 2. First variation is auto-selected (index 0)
      // 3. Ticket exists
      // 4. Front image exists
      // 5. Not currently loading or modifying
      if (
        state.backVariations &&
        state.backVariations.length > 0 &&
        state.selectedBackVariationIndex === 0 &&
        state.ticketId &&
        state.imageUrl &&
        state.imagePath &&
        !state.loading &&
        !state.isModifying
      ) {
        const firstVariation = state.backVariations[0];
        if (firstVariation) {
          isUpdatingTicketRef.current = true;
          try {
            await updateGeneratedTicket(
              state.ticketId,
              state.imageUrl,
              state.imagePath,
              firstVariation.imageUrl,
              firstVariation.imagePath
            );
          } catch (error) {
            logger.error(
              "Failed to auto-update ticket for back variation",
              {
                component: "useAutoTicketManagement",
                metadata: { ticketId: state.ticketId, variationIndex: 0 },
              },
              error instanceof Error ? error : new Error(String(error))
            );
            // Don't fail the entire flow - user can still manually select
          } finally {
            isUpdatingTicketRef.current = false;
          }
        }
      }
    };

    autoUpdateBackTicket();
  }, [
    state.backVariations,
    state.selectedBackVariationIndex,
    state.persistedBackTicketId,
    state.backStale,
    state.ticketId,
    state.imageUrl,
    state.imagePath,
    state.loading,
    state.isModifying,
    updateGeneratedTicket,
  ]);
}
